import { useState, useRef } from "react";
import ToolbarElements from "./toolbarElements";
import { Button, Tooltip } from "flowbite-react";
import { Clipboard, Copy } from "../../layouts/icons";

function Editor() {
  const editorRef = useRef(null);
  const [html, setHtml] = useState("");
  const [copyText, setCopyText] = useState("Copy Link");

  const [colorValues, setColorValues] = useState({
    foreColor: "#000000",
    hiliteColor: "#ffffff",
  });
  const { toolbarBtns, toolbarColors, toolbarFontSizes, toolbarHeadings } =
    ToolbarElements();

  const handleColorChange = (action, value) => {
    formatText(action, value);
    setColorValues((prev) => ({
      ...prev,
      [action]: value,
    }));
  };

  const formatText = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const convertToHtml = () => {
    if (editorRef.current) {
      setHtml(editorRef.current.innerHTML);
    }
  };

  const copyHtml = () => {
    navigator.clipboard.writeText(html);
    setCopyText("Content copied");
    setTimeout(() => {
      setCopyText("Copy content");
    }, 2000);
  };

  return (
    <div className="container">
      <div className="max-w-full mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-16  sm:items-top sm:justify-center">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-5 border border-gray-200 p-3 rounded-lg bg-white shadow-sm">
          {/* Headings */}
          <select
            onChange={(e) => {
              formatText("formatBlock", e.target.value);
            }}
            defaultValue=""
            className="border border-gray-200 rounded-xl"
          >
            <option value="" disabled>
              Headings
            </option>
            {toolbarHeadings.map((item, index) => (
              <option key={index} value={item.value}>
                {item.title}
              </option>
            ))}
          </select>

          {/* Font Size */}
          <select
            onChange={(e) => formatText("fontSize", e.target.value)}
            defaultValue=""
            className="border border-gray-200 rounded-xl"
          >
            <option value="" disabled>
              Font Size
            </option>
            {toolbarFontSizes.map((item, index) => {
              return (
                <option key={index} value={item.value}>
                  {item.title}
                </option>
              );
            })}
          </select>

          {/* Other buttons */}
          {toolbarBtns.map((item, index) => {
            return (
              <button key={index} onClick={() => formatText(item.action)}>
                {item.title}
              </button>
            );
          })}

          {/* Link */}
          <button
            onClick={() => {
              const url = prompt("Enter the link URL:");
              if (url) formatText("createLink", url);
            }}
          >
            <Copy />
          </button>

          {/* Color */}
          <div className="flex items-center gap-2">
            {toolbarColors.map((item, index) => (
              <label
                key={index}
                className="flex items-center gap-1 cursor-pointer border border-gray-100 rounded-xl p-1"
              >
                <span className="text-xs select-none">{item.title}</span>
                <div className="relative w-8 h-8">
                  <input
                    type={item.type}
                    onChange={(e) =>
                      handleColorChange(item.action, e.target.value)
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />

                  <span
                    className="block w-full h-full rounded-full border border-gray-300"
                    style={{
                      backgroundColor: colorValues[item.action] || "#000000",
                    }}
                  />
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Editable area */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning={true}
          className="border mt-3  border-gray-300 rounded-lg p-2.5 min-h-[200px] list-inside whitespace-pre-wrap"
          data-placeholder="Type something here..."
        ></div>

        {/* Buttons */}
        <div className="theme-variant mt-3 flex gap-2">
          <Button onClick={convertToHtml}>Convert</Button>
        </div>

        {/* Output */}
        {html && (
          <div className="mt-8">
            <div className="flex justify-between">
              <h3 className="text-lg font-semibold">Output</h3>
              <Tooltip content={copyText} trigger="hover" placement="left">
                <Button onClick={copyHtml}>
                  <Clipboard /> Copy
                </Button>{" "}
              </Tooltip>
            </div>
            <textarea
              readOnly
              value={html}
              className="border border-gray-300 mt-1 rounded-lg w-full h-[250px] font-mono"
            />
          </div>
        )}
      </div>

      {/* Inline CSS */}
      <style>{`
          [contenteditable] ul {
            padding-left: 20px;
            margin: 5px 0;
             list-style-type: disc;
          }
          [contenteditable] ol {
            padding-left: 20px;
            margin: 5px 0;
            list-style-type: decimal; 
          }
          [contenteditable] li {
            margin: 3px 0;
          }
        [contenteditable="true"] a {
            color: #2563eb; /* Tailwind blue-600 */
            text-decoration: underline;
            cursor: pointer;
            }
        [contenteditable="true"] a:hover {
        color: #1d4ed8; /* Tailwind blue-700 */
        }
        `}</style>
    </div>
  );
}

export default Editor;
